import React from "react";
import { CheckCircle } from "lucide-react";

const pricingPlans = [
  {
    name: "Starter",
    price: "Free",
    period: "forever",
    description: "Ideal for individuals and small teams.",
    features: ["5 Projects", "10 Team Members", "Kanban Boards", "Real-time Sync", "Basic Support"],
    cta: "Get Started",
    highlight: false,
  },
  {
    name: "Pro",
    price: "$12",
    period: "/mo",
    description: "Advanced tools for teams that need to scale.",
    features: ["Unlimited Projects", "Unlimited Members", "Credentials Vault", "AI Ticket Extraction", "@Mentions", "Priority Support"],
    cta: "Start Free Trial",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "Security and support for large organizations.",
    features: ["Everything in Pro", "SSO & SAML Auth", "Custom Integrations", "Advanced Audit Logs", "SLA Guarantee", "Dedicated Support"],
    cta: "Contact Sales",
    highlight: false,
  },
];

const MarketingPricingCards = ({ className = "" }) => {
  return (
    <section className={`curved-section shell-white section-animate ${className}`}>
      <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="section-eyebrow reveal">Pricing</span>
          <h2 className="reveal stagger-1 mt-4 mb-6">
            Pricing that scales <span className="text-navy">with your team</span>
          </h2>
          <p className="text-lead reveal stagger-2">
            Simple, transparent pricing. No per-seat charges, no hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
          {pricingPlans.map((plan, idx) => (
            <div
              key={plan.name}
              className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 reveal stagger-${idx + 1} ${
                plan.highlight
                  ? "bg-[#222875] text-white shadow-[0_20px_50px_rgba(34,40,117,0.25)] scale-105 z-10 border-0"
                  : "bg-white text-gray-900 border border-gray-100 shadow-xl hover:shadow-2xl"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest py-1 px-4 rounded-full">
                  {plan.badge}
                </div>
              )}

              <div className="mb-8">
                <h3 className={`text-2xl font-bold mb-2 ${plan.highlight ? "text-[#CBEFFF]" : "text-[#222875]"}`}>{plan.name}</h3>
                <p className={`text-sm ${plan.highlight ? "text-blue-100" : "text-gray-500"}`}>
                  {plan.description}
                </p>
              </div>

              <div className="mb-8 flex items-baseline gap-1">
                <span className="text-5xl font-black">{plan.price}</span>
                <span className={`text-lg ${plan.highlight ? "text-blue-200" : "text-gray-500"}`}>
                  {plan.period}
                </span>
              </div>

              <ul className="space-y-4 mb-10 flex-1">
                {plan.features.map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-3 text-sm font-medium">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 ${plan.highlight ? "text-green-400" : "text-green-500"}`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-4 px-6 rounded-lg text-center font-bold transition-all ${
                  plan.highlight
                    ? "bg-white text-[#222875] hover:bg-[#CBEFFF] shadow-lg"
                    : "bg-[#222875] text-white hover:bg-opacity-90 shadow-md"
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12 reveal">
          <p className="text-gray-500 text-sm">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MarketingPricingCards;
